/**
 * iter163 (2026-07-23) — Marketing Attribution Debug Page.
 *
 * Hidden owner tool at `/marketing/attribution-check`. Not linked from any
 * nav or footer. Shows exactly what UTM/gclid/fbclid attribution bundle
 * the app has captured for the current visitor, PLUS a URL simulator that
 * lets an owner paste any inbound campaign URL and see what would be
 * captured — great for validating that a newsletter or social post will
 * attribute correctly BEFORE spending on the campaign.
 *
 * Zero impact on GA4 / GTM / AddPeople tracking — this page only READS
 * localStorage.pp_attribution and simulates the same parsing that
 * `Analytics.captureFirstTouchAttribution()` performs.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Copy, ExternalLink, RefreshCw, Trash2 } from 'lucide-react';
import Analytics from '../utils/analytics';

const ATTR_KEY = 'pp_attribution';

function parseUrlForAttribution(rawUrl) {
  try {
    const url = new URL(rawUrl);
    const params = url.searchParams;
    return {
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_term: params.get('utm_term') || null,
      utm_content: params.get('utm_content') || null,
      gclid: params.get('gclid') || null,
      fbclid: params.get('fbclid') || null,
      landing_url: rawUrl,
      landed_at: '(would be captured on landing)',
      referrer: '(would be captured on landing)',
    };
  } catch {
    return null;
  }
}

function ClassBucket(bundle) {
  // Mirrors the backend's admin_campaign_roi_routes.py bucketing logic.
  if (!bundle) return '(invalid url)';
  if (bundle.utm_source) return `${bundle.utm_source} / ${bundle.utm_medium || '—'} / ${bundle.utm_campaign || '—'}`;
  if (bundle.gclid) return 'google_ads (gclid)';
  if (bundle.fbclid) return 'facebook_ads (fbclid)';
  return '(direct)';
}

export default function MarketingAttributionCheckPage() {
  const [currentAttr, setCurrentAttr] = useState(null);
  const [simulateUrl, setSimulateUrl] = useState('');
  const [simulated, setSimulated] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const loadCurrent = () => {
    try {
      const raw = window.localStorage.getItem(ATTR_KEY);
      setCurrentAttr(raw ? JSON.parse(raw) : null);
    } catch {
      setCurrentAttr(null);
    }
  };

  useEffect(() => { loadCurrent(); }, []);

  const handleSimulate = () => {
    if (!simulateUrl.trim()) { setSimulated(null); return; }
    const parsed = parseUrlForAttribution(simulateUrl.trim());
    setSimulated(parsed);
  };

  const handleClearStored = () => {
    try {
      window.localStorage.removeItem(ATTR_KEY);
      loadCurrent();
    } catch { /* ignore */ }
  };

  const handleRecapture = () => {
    Analytics.captureFirstTouchAttribution();
    loadCurrent();
  };

  const copy = (label, value) => {
    try {
      navigator.clipboard.writeText(String(value ?? ''));
      setCopiedField(label);
      setTimeout(() => setCopiedField(null), 1500);
    } catch { /* ignore */ }
  };

  const renderBundle = (bundle, testIdPrefix) => {
    if (!bundle) {
      return (
        <div className="text-sm text-gray-500 italic" data-testid={`${testIdPrefix}-empty`}>
          No attribution bundle captured yet.
        </div>
      );
    }
    const rows = [
      ['utm_source', bundle.utm_source],
      ['utm_medium', bundle.utm_medium],
      ['utm_campaign', bundle.utm_campaign],
      ['utm_term', bundle.utm_term],
      ['utm_content', bundle.utm_content],
      ['gclid', bundle.gclid],
      ['fbclid', bundle.fbclid],
      ['referrer', bundle.referrer],
      ['landing_url', bundle.landing_url],
      ['landed_at', bundle.landed_at],
    ];
    return (
      <div className="space-y-1.5 font-mono text-sm" data-testid={`${testIdPrefix}-bundle`}>
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-start gap-3">
            <span className="text-gray-500 w-28 flex-shrink-0">{k}</span>
            <span className={`flex-1 break-all ${v ? 'text-gray-900' : 'text-gray-400 italic'}`} data-testid={`${testIdPrefix}-${k}`}>
              {v !== null && v !== undefined && v !== '' ? String(v) : '(none)'}
            </span>
            {v && (
              <button
                onClick={() => copy(`${testIdPrefix}-${k}`, v)}
                className="text-gray-400 hover:text-emerald-600 flex-shrink-0"
                title="Copy"
                data-testid={`${testIdPrefix}-copy-${k}`}
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2">
          <span className="text-xs text-gray-500 uppercase tracking-wide">Marketing Dashboard bucket:</span>
          <span className="font-semibold text-emerald-700 text-sm" data-testid={`${testIdPrefix}-bucket`}>
            {ClassBucket(bundle)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <main className="min-h-screen bg-slate-50 pb-20" data-testid="marketing-attribution-check-page">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-6"
          data-testid="marketing-attr-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Marketing Attribution Check
          </h1>
          <p className="text-sm text-gray-600 max-w-2xl">
            Owner-only tool. Confirms exactly what first-touch attribution the app has captured for
            your current session, and lets you simulate any campaign URL to preview how it will
            attribute in the Marketing Dashboard ROI tile.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            This page is hidden — not linked from any navigation. Bookmark the URL.
          </p>
        </header>

        {/* 1. Current visitor attribution */}
        <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6" data-testid="attr-current-section">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Your current attribution bundle</h2>
              <p className="text-xs text-gray-500 mt-1">
                Read from <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px]">localStorage.{ATTR_KEY}</code>
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleRecapture}
                className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1.5"
                data-testid="attr-recapture-btn"
                title="Runs Analytics.captureFirstTouchAttribution() — idempotent, only captures if bundle is missing"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Recapture
              </button>
              <button
                onClick={handleClearStored}
                className="text-xs px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg flex items-center gap-1.5"
                data-testid="attr-clear-btn"
                title="Wipes localStorage.pp_attribution so the next landing page captures fresh"
              >
                <Trash2 className="w-3.5 h-3.5" /> Clear stored
              </button>
            </div>
          </div>
          {renderBundle(currentAttr, 'attr-current')}
          {copiedField && copiedField.startsWith('attr-current') && (
            <p className="text-xs text-emerald-600 mt-2">Copied to clipboard.</p>
          )}
        </section>

        {/* 2. URL simulator */}
        <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6" data-testid="attr-simulator-section">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Simulate a campaign URL</h2>
          <p className="text-xs text-gray-500 mb-4">
            Paste any inbound URL (newsletter link, social post, ad landing) and preview exactly how
            it will attribute. Does not modify your stored bundle.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="url"
              value={simulateUrl}
              onChange={(e) => setSimulateUrl(e.target.value)}
              placeholder="https://pricepalcompare.com/pricing?utm_source=newsletter&utm_campaign=jan_promo&utm_medium=email"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent font-mono"
              data-testid="attr-simulate-input"
            />
            <button
              onClick={handleSimulate}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700"
              data-testid="attr-simulate-btn"
            >
              Simulate
            </button>
          </div>

          {simulated === null && simulateUrl && (
            <p className="text-sm text-red-600 mt-3" data-testid="attr-simulate-invalid">
              That URL doesn&apos;t look valid. Include the full URL with <code>https://</code>.
            </p>
          )}
          {simulated && (
            <div className="mt-5 pt-5 border-t border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Would capture:</p>
              {renderBundle(simulated, 'attr-simulate')}
            </div>
          )}
        </section>

        {/* 3. Quick-test presets */}
        <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6" data-testid="attr-presets-section">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Common campaign patterns</h2>
          <p className="text-xs text-gray-500 mb-4">
            Click any preset to open it in a new tab — landing there will stamp the corresponding
            attribution in that tab&apos;s localStorage.
          </p>
          <div className="grid gap-2">
            {[
              { label: 'Newsletter (email)', url: '/?utm_source=newsletter&utm_medium=email&utm_campaign=weekly_shop' },
              { label: 'Facebook organic post', url: '/pricing?utm_source=facebook&utm_medium=social&utm_campaign=organic' },
              { label: 'Instagram story CTA', url: '/community?utm_source=instagram&utm_medium=social&utm_campaign=nhs_launch' },
              { label: 'Google Ads (gclid)', url: '/?gclid=EAIaIQobChMI_test_gclid_value' },
              { label: 'Facebook Ads (fbclid)', url: '/pricing?fbclid=IwAR_test_fbclid_value' },
              { label: 'AddPeople example (Google Ads + utm)', url: '/?utm_source=addpeople&utm_medium=cpc&utm_campaign=fuel_july&gclid=EAIaIQobChMI_addpeople' },
            ].map(({ label, url }) => (
              <a
                key={label}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition"
                data-testid={`attr-preset-${label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
              >
                <div>
                  <div className="text-sm font-semibold text-gray-900">{label}</div>
                  <div className="text-xs font-mono text-gray-500 mt-0.5 break-all">{url}</div>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 ml-3" />
              </a>
            ))}
          </div>
        </section>

        <p className="text-center text-xs text-gray-400 mt-8">
          iter163 · 2026-07-23 · Read-only debug page. Not indexed by search engines.
        </p>
      </div>
    </main>
  );
}
